import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { SystemRoleName } from '@evimo/types/enums/system-role-name';
import { useAuth } from '~/app/auth/context/AuthContext';
import { ErrorBox } from '~/app/components/common/Error/ErrorBox';
import { Loader } from '~/app/components/common/Loader/Loader';
import { OrganizationService } from '~/app/services/organization/organization.service';
import { AssetService } from '~/app/services/asset/asset.service';
import { PipelineDebugPageView } from './PipelineDebugPageView';

const ALLOWED_ROLES = new Set<string>([SystemRoleName.SUPER_ADMIN]);

export function PipelineDebugPageContainer() {
  const { user, isLoading: isAuthLoading } = useAuth();
  const isSuperAdmin = user?.roleName === SystemRoleName.SUPER_ADMIN;
  const [organizationId, setOrganizationId] = useState('');
  const [assetId, setAssetId] = useState('');

  const {
    data: organizations = [],
    isLoading: isLoadingOrganizations,
    error: organizationsError,
  } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => OrganizationService.findAllOrganizations(),
    enabled: isSuperAdmin && !isAuthLoading,
  });

  const { data: assets = [], isLoading: isLoadingAssets } = useQuery({
    queryKey: ['assets', 'lookup', organizationId],
    queryFn: () => AssetService.findAllByOrganizationId(organizationId),
    enabled: !!organizationId,
  });

  useEffect(() => {
    if (isSuperAdmin && organizations.length > 0 && !organizationId) {
      setOrganizationId(organizations[0].id);
      return;
    }

    if (!isSuperAdmin && user?.organizationId) {
      setOrganizationId(user.organizationId);
    }
  }, [isSuperAdmin, organizations, organizationId, user?.organizationId]);

  useEffect(() => {
    setAssetId('');
  }, [organizationId]);

  useEffect(() => {
    if (assets.length > 0 && !assetId) {
      setAssetId(assets[0].id);
    }
  }, [assets, assetId]);

  const handleOrganizationIdChange = (nextOrganizationId: string) => {
    setOrganizationId(nextOrganizationId);
    setAssetId('');
  };

  if (
    isAuthLoading ||
    (isSuperAdmin && isLoadingOrganizations) ||
    (organizationId && isLoadingAssets && !assetId)
  ) {
    return <Loader />;
  }

  if (!user || !ALLOWED_ROLES.has(user.roleName)) {
    return <Navigate to="/home" replace />;
  }

  if (isSuperAdmin && organizationsError) {
    return (
      <ErrorBox
        title="Failed to load organizations"
        description={organizationsError.message}
      />
    );
  }

  return (
    <PipelineDebugPageView
      isSuperAdmin={isSuperAdmin}
      organizationId={organizationId}
      organizations={organizations}
      onOrganizationIdChange={handleOrganizationIdChange}
      assetId={assetId}
      assets={assets}
      onAssetIdChange={setAssetId}
      isLoadingAssets={isLoadingAssets}
    />
  );
}
